import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import SortOptionsMedications from "models/content-wrapper/sort-options-medications";
import {
  MedicationDiscontinued,
  MedicationSearchFilter,
  MedicationSearchResults,
  MedicationStatusUpdate,
  PatientMedication,
} from "pages/patient-chart/medications/patient-medication";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { Observable } from "rxjs";

@Injectable()
export class PatientMedicationsService {
  private apiUrl: string;

  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.apiUrl = config.getConfig().apiUrl;

    if (
      this.config.getConfig().isLocal &&
      this.config.getConfig().localEndpoints.patientMedications
    ) {
      this.apiUrl = this.config.getConfig().localEndpoints.patientMedications;
    } else {
      this.apiUrl = this.config.getConfig().apiUrl + "/patient-medications";
    }
  }

  /**
   * Returns a Patient Medication by ID
   * @param patientChartId  the chart
   * @param medicationId the medication id
   * @returns an interface matching the Patient Medication
   */
  public getMedication(
    patientChartId: number,
    medicationId: string,
    currentDate: string
  ): Observable<PatientMedication> {
    return this.http.get<PatientMedication>(
      `${this.apiUrl}/patient-charts/${patientChartId}/medications/${medicationId}?currentDate=${currentDate}`
    );
  }

  public deleteMedication(
    patientChartId: number,
    medicationId: string
  ): Observable<any> {
    return this.http.delete<any>(
      `${this.apiUrl}/patient-charts/${patientChartId}/medications/${medicationId}`
    );
  }

  public createMedication(
    patientChartId: number,
    medication: PatientMedication
  ): Observable<PatientMedication> {
    return this.http.post<PatientMedication>(
      `${this.apiUrl}/patient-charts/${patientChartId}/medications`,
      medication
    );
  }

  public updateMedication(
    patientChartId: number,
    medicationId: string,
    medication: PatientMedication
  ): Observable<PatientMedication> {
    return this.http.put<PatientMedication>(
      `${this.apiUrl}/patient-charts/${patientChartId}/medications/${medicationId}`,
      medication
    );
  }

  public discontinueMedication(
    patientChartId: number,
    medicationId: string,
    discontinued: MedicationDiscontinued
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/patient-charts/${patientChartId}/medications/${medicationId}/discontinue`,
      discontinued
    );
  }

  public updateMedicationStatus(
    patientChartId: number,
    medicationId: string,
    statusUpdate: MedicationStatusUpdate,
    currentDate: string
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/patient-charts/${patientChartId}/medications/${medicationId}/update-status?currentDate=${currentDate}`,
      statusUpdate
    );
  }

  /**
   * Gets patient medications by filter
   * @param patientChartId the patient to get the data for
   * @param sf search filter
   * @param so sort options
   */
  public getMedications(
    patientChartId: number,
    sf: MedicationSearchFilter,
    so: SortOptionsMedications,
    currentDate: string
  ): Observable<MedicationSearchResults> {
    const q = `q=${sf.searchString}&from=${sf.fromDate}&to=${
      sf.toDate
    }&status=${sf.status}&source=${sf.source}&f=${sf.fromRow}&t=${
      sf.toRow
    }&field=${so.column}&order=${
      so[so.column]
    }&currentDate=${currentDate}&showDiscontinued=${sf.showDiscontinued}`;

    return this.http
      .get<MedicationSearchResults>(
        `${this.apiUrl}/patient-charts/${patientChartId}/medications?${q}`
      )
      .map((x: any) => {
        return {
          searchFilter: x.searchResult.searchFilter,
          patientMedication: x.searchResult.patientMedication,
          toRow: x.searchResult.toRow,
          fromRow: x.searchResult.fromRow,
          totalRows: x.searchResult.totalRows,
        };
      });
  }
}



// WEBPACK FOOTER //
// ./src/providers/patient-charts/patient-medications.service.ts